import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Enterprise from "../../Enterprise";
import Masters from "../../masters/Masters";
import EnterpriseConfigurationSettings from "./EnterpriseConfigurationSettings";
import { usePermissions } from "../../../../rbac/PermissionsContext";
import { DsEmptyState } from "../../../../design-system";
import { ADMIN_SHELL_NAV_EVENT } from "../../../../utils/adminShellNav";

// Tab key (UI) -> canonical catalog screen key under "configuration".
// Keep this in sync with src/rbac/catalog.js -> configuration.screens.
const CONFIG_TAB_TO_SCREEN = {
  enterprise: "enterprise",
  masters: "masters",
  settings: "settings",
};

const VALID_TABS = new Set(["enterprise", "masters", "settings"]);

const Configuration = ({ darkMode }) => {
  const [activeConfigTab, setActiveConfigTab] = useState(() => {
    try {
      const tab = new URLSearchParams(window.location.search).get("tab");
      if (VALID_TABS.has(tab)) return tab;
      // Legacy link: codes live under Attendance → Settings → Codes and devices
      if (tab === "attendanceCodes") return "enterprise";
    } catch {
      /* ignore */
    }
    return "enterprise";
  });
  const [searchParams] = useSearchParams();
  const { hasScreenAccess } = usePermissions();

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (VALID_TABS.has(tab)) {
      setActiveConfigTab(tab);
    } else if (tab === "attendanceCodes") {
      setActiveConfigTab("enterprise");
    }
  }, [searchParams]);

  useEffect(() => {
    const onShellNav = (event) => {
      const tab = event?.detail?.tab;
      if (event?.detail?.module !== "configuration") return;
      if (VALID_TABS.has(tab)) {
        setActiveConfigTab(tab);
      } else if (tab === "attendanceCodes") {
        setActiveConfigTab("enterprise");
      }
    };
    window.addEventListener(ADMIN_SHELL_NAV_EVENT, onShellNav);
    return () => window.removeEventListener(ADMIN_SHELL_NAV_EVENT, onShellNav);
  }, []);

  const canViewSettings =
    hasScreenAccess("configuration", "settings", "view") ||
    hasScreenAccess("configuration", "settings", "edit") ||
    hasScreenAccess("configuration", "config_set_product_theme", "view") ||
    hasScreenAccess("configuration", "config_set_product_theme", "edit") ||
    hasScreenAccess("configuration", "config_set_org_preferences", "view") ||
    hasScreenAccess("configuration", "config_set_org_preferences", "edit");

  const allowedTabs = useMemo(
    () =>
      [
        { key: "enterprise", label: "Enterprise" },
        { key: "masters", label: "Masters" },
        { key: "settings", label: "Settings" },
      ].filter((tab) => {
        if (tab.key === "settings") return canViewSettings;
        return hasScreenAccess("configuration", CONFIG_TAB_TO_SCREEN[tab.key], "view");
      }),
    [hasScreenAccess, canViewSettings]
  );

  useEffect(() => {
    if (allowedTabs.length === 0) return;
    const isActiveAllowed = allowedTabs.some((t) => t.key === activeConfigTab);
    if (!isActiveAllowed) setActiveConfigTab(allowedTabs[0].key);
  }, [allowedTabs, activeConfigTab]);

  return (
    <div className="ds-page">
      <div className="ds-page-shell">
        <div className="ds-page-body">
          <aside className="ds-page-aside">
            <div className="ds-page-aside-head">
              <p className="ds-small px-1 font-bold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                Enterprise
              </p>
              <p className="ds-caption mt-1 px-1 leading-snug text-[hsl(var(--ds-muted-fg))]">
                Company profile, masters, and organisation settings.
              </p>
            </div>
            <div className="ds-page-aside-nav">
              {allowedTabs.length === 0 ? (
                <div className="mt-2 rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-3 py-3 text-xs text-[hsl(var(--ds-muted-fg))]">
                  You do not have access to any configuration section. Please contact your
                  administrator.
                </div>
              ) : (
                allowedTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveConfigTab(tab.key)}
                    className={
                      activeConfigTab === tab.key
                        ? "ds-page-nav-item ds-page-nav-item-active"
                        : "ds-page-nav-item"
                    }
                  >
                    {tab.label}
                  </button>
                ))
              )}
            </div>
          </aside>

          <div className="ds-page-main">
            <div className="ds-page-content-flush">
              {allowedTabs.length === 0 && (
                <div className="flex h-full items-center justify-center">
                  <DsEmptyState
                    title="No configuration access"
                    description="You do not currently have permission to view any section of Configuration. Please contact your administrator to request access."
                  />
                </div>
              )}

              {activeConfigTab === "enterprise" &&
                hasScreenAccess("configuration", "enterprise", "view") && (
                  <Enterprise darkMode={darkMode} />
                )}
              {activeConfigTab === "masters" &&
                hasScreenAccess("configuration", "masters", "view") && (
                  <Masters darkMode={darkMode} initialTab="masters" allowedTabs={["masters"]} />
                )}

              {activeConfigTab === "settings" && canViewSettings && (
                  <EnterpriseConfigurationSettings darkMode={darkMode} />
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Configuration;
